// A service boat's journey, for the strip on its Service card: Expected → At the shipyard → In service →
// Ready → Delivered. It follows the boat's own "@ Shipyard" value first (Expected, at the yard, gone),
// then how far its works are (their Status / Done), then its dates (its estimated and actual arrival,
// Start, Expected delivery, Delivery).
// So the strip always agrees with what the sheet says about the boat, not with its build in production.
import type { JourneyStep } from '@technohull/shared';

export const SERVICE_STAGES = [
  { key: 'expected', label: 'Expected' },
  { key: 'yard', label: 'At the shipyard' },
  { key: 'works', label: 'In service' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Delivered' },
] as const;

/** where the boat is, from its @ Shipyard value: on its way, at the yard, or gone again. */
export type YardState = 'expected' | 'here' | 'gone' | 'unknown';

const EXPECTED = /expect|αναμ[εέ]ν|booked|coming|soon|pending|προγραμμ/i;
const GONE = /deliver|left|gone|return|away|παρ[αά]δ|[εέ]φυγ|αναχ[ωώ]ρ/i;
const HERE = /^(?:yes|y|ναι|✓|true)$|here|arriv|present|in\s*yard|at\s*(?:the\s*)?(?:ship)?yard|in\s*shipyard|ναυπηγ|εδ[ωώ]|[εέ]φτασ/i;

/** a @ Shipyard cell (a dropdown's text, or a checkbox) as a YardState. "No" / unticked is unknown: the
 *  boat could be on its way or gone, so the dates decide. */
export function yardStateOf(value: string | boolean | undefined | null): YardState {
  if (value === true) return 'here';
  if (value == null || value === false) return 'unknown';
  const s = String(value).trim();
  if (!s || /^(?:no|not|όχι|οχι)\b/i.test(s)) return 'unknown';
  if (EXPECTED.test(s)) return 'expected';
  if (GONE.test(s)) return 'gone';
  if (HERE.test(s)) return 'here';
  return 'unknown';
}

export interface ServiceDates {
  /** the boat's service start ("Start Date"). */
  start?: string;
  /** when it's due back ("Expected delivery date"). */
  expected?: string;
  /** when it was / is to be handed back ("Delivery date"). */
  delivery?: string;
  /** when it is expected at the yard ("Estimated Vessel Arrival Date"). */
  expectedArrival?: string;
  /** when it came ("Actual Vessel Arrival Date"). */
  arrival?: string;
}
export interface WorksRoll { done: number; total: number; ok: number; warn: number }
export interface ServiceJourney { steps: JourneyStep[]; label: string; tone: 'warn' | 'err' | null; summary: string }

const isoDay = (s: string | undefined): string => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '');

/** the steps with everything before `at` done, `at` itself in `state`, the rest still to come. */
function stepsAt(at: number, state: JourneyStep['state']): JourneyStep[] {
  return SERVICE_STAGES.map((s, i) => ({ key: s.key, label: s.label, state: i < at ? 'done' : i === at ? state : 'todo' }));
}

/** The journey for one boat, or null when nothing says where it is (no @ Shipyard value, no dates, no
 *  work started), so the card draws no strip rather than a guess. `today` is the local ISO day; `day`
 *  formats an ISO day for the label. */
export function serviceJourney(yard: YardState, works: WorksRoll, dates: ServiceDates, today: string, day: (iso: string) => string): ServiceJourney | null {
  const start = isoDay(dates.start);
  const arrived = isoDay(dates.arrival);
  // when it's expected: its estimated arrival, else its start (the yard's way before it had one)
  const eta = isoDay(dates.expectedArrival) || start;
  const due = isoDay(dates.expected) || isoDay(dates.delivery);
  const delivered = isoDay(dates.delivery);
  const started = works.done > 0 || works.ok > 0 || works.warn > 0;
  const allDone = works.total > 0 && works.done >= works.total;

  let where = yard;
  if (where === 'unknown') {
    if (delivered && delivered <= today && (allDone || works.total === 0)) where = 'gone';
    else if (started || (start && start <= today) || (arrived && arrived <= today)) where = 'here';
    else if (eta) where = 'expected';
    else return null;
  }

  const out = (steps: JourneyStep[], label: string, tone: ServiceJourney['tone'] = null): ServiceJourney => ({
    steps, label, tone, summary: `Service: ${label}`,
  });

  if (where === 'gone') {
    return out(SERVICE_STAGES.map((s) => ({ key: s.key, label: s.label, state: 'done' })), delivered && delivered <= today ? `Delivered ${day(delivered)}` : 'Delivered');
  }
  if (where === 'expected') {
    if (eta && eta < today) return out(stepsAt(0, 'hold'), `Expected since ${day(eta)}`, 'warn');
    return out(stepsAt(0, 'current'), eta ? `Expected ${day(eta)}` : 'Expected');
  }
  // at the shipyard
  const overdue = !!due && due < today;
  if (allDone) {
    if (overdue) return out(stepsAt(3, 'late'), 'Delivery overdue', 'err');
    return out(stepsAt(3, 'current'), due ? `Ready, delivers ${day(due)}` : 'Ready for delivery');
  }
  if (started || (start && start <= today)) {
    const n = works.total > 0 ? ` · ${works.done}/${works.total} done` : '';
    if (overdue) return out(stepsAt(2, 'late'), `In service${n}, delivery overdue`, 'err');
    return out(stepsAt(2, 'current'), `In service${n}`);
  }
  if (overdue) return out(stepsAt(1, 'late'), 'At the shipyard, delivery overdue', 'err');
  return out(stepsAt(1, 'current'), 'At the shipyard');
}

/** a step's state in words, for its tooltip ("In service: in progress"). */
export function serviceStepWord(step: JourneyStep): string {
  switch (step.state) {
    case 'done': return 'done';
    case 'current': return 'now';
    case 'hold': return 'waiting';
    case 'late': return 'overdue';
    default: return 'to come';
  }
}
